import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Loader2, Pencil, Plus } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { EMPTY, displayValue } from "@/lib/finance";
import { useCatalogs } from "@/lib/lookups";
import { AppHeader } from "@/components/layout/AppHeader";
import { NamtechFooter } from "@/components/poc/NamtechFooter";
import { FieldsForm } from "@/components/finance/FieldsForm";
import { MovementForm } from "@/components/finance/MovementForm";
import { MovementsList } from "@/components/finance/MovementsList";
import { Timeline } from "@/components/finance/Timeline";
import { StatusBadge } from "@/components/finance/StatusBadge";

const Section = ({ title, action, children, testid }) => (
  <section data-testid={testid} className="c-card p-4">
    <div className="flex items-center justify-between mb-2">
      <h2 className="font-mono text-[10px] tracking-[0.2em] uppercase c-muted">{title}</h2>
      {action}
    </div>
    {children}
  </section>
);

const FieldsList = ({ fields, item, cats, onEdit }) => (
  <dl className="divide-y c-line">
    {fields.map((f) => {
      const v = displayValue(f, item, cats);
      return (
        <button key={f.key} type="button" onClick={onEdit} data-testid={`detail-field-${f.key.replace(/_/g, "-")}`}
          className="w-full flex justify-between gap-4 py-2.5 text-left">
          <dt className="text-sm c-text-2 shrink-0">{f.label}</dt>
          <dd className={`text-sm text-right ${v == null ? "italic c-muted" : "font-medium"}`}>{v ?? EMPTY}</dd>
        </button>
      );
    })}
  </dl>
);

export const EntityDetail = ({ endpoint, backTo, eyebrow, titleKey, fields, moveTypes, Summary }) => {
  const { id } = useParams();
  const [item, setItem] = useState(null);
  const [editing, setEditing] = useState(false);
  const [adding, setAdding] = useState(false);
  const cats = useCatalogs();
  const labels = useMemo(() => Object.fromEntries(fields.map((f) => [f.key, f.label])), [fields]);

  const load = useCallback(() => api.get(`${endpoint}/${id}`).then((r) => setItem(r.data)).catch((e) => toast.error(errMsg(e))), [endpoint, id]);
  useEffect(() => { load(); }, [load]);

  const save = async (payload) => {
    if (!Object.keys(payload).length) return setEditing(false);
    try {
      setItem((await api.patch(`${endpoint}/${id}`, payload)).data);
      setEditing(false);
      toast.success("Cambios guardados");
    } catch (e) { toast.error(errMsg(e)); }
  };
  const addMove = async (payload) => {
    setItem((await api.post(`${endpoint}/${id}/movements`, payload)).data);
    setAdding(false);
    toast.success("Movimiento registrado");
  };

  const voidMove = async (mv, reason) => {
    try {
      setItem((await api.post(`${endpoint}/${id}/movements/${mv.id}/void`, { reason })).data);
      toast.success("Movimiento anulado");
    } catch (e) { toast.error(errMsg(e)); }
  };

  if (!item) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin c-muted" /></div>;

  return (
    <div data-testid="entity-detail-page" className="min-h-screen max-w-md mx-auto flex flex-col">
      <AppHeader eyebrow={eyebrow} title={item[titleKey]} back={backTo} />
      <main className="flex-1 px-4 pt-2 space-y-4">
        <div className="flex items-center gap-2"><StatusBadge status={item.status} testid="detail-status-badge" /></div>
        <Summary item={item} />
        <Section title="Datos" testid="detail-fields" action={
          <button data-testid="detail-edit-btn" onClick={() => setEditing(true)} className="flex items-center gap-1 text-xs font-semibold c-accent"><Pencil className="w-3.5 h-3.5" /> Editar</button>}>
          <FieldsList fields={fields} item={item} cats={cats} onEdit={() => setEditing(true)} />
        </Section>
        <Section title={`Movimientos (${item.movements.length})`} testid="detail-movements" action={
          <button data-testid="add-movement-btn" onClick={() => setAdding(true)} className="flex items-center gap-1 text-xs font-semibold c-accent"><Plus className="w-3.5 h-3.5" /> Agregar</button>}>
          <MovementsList movements={item.movements} labels={moveTypes} onVoid={voidMove} />
        </Section>
        <Section title="Historial inmutable" testid="detail-timeline"><Timeline events={item.timeline} labels={labels} /></Section>
        {item.source_note && (
          <Section title="Origen (Excel)" testid="detail-source-note"><p className="text-xs font-mono c-text-2 break-words">{item.source_note}</p></Section>
        )}
      </main>
      <div className="px-4"><NamtechFooter /></div>
      <FieldsForm open={editing} onOpenChange={setEditing} title="Editar" fields={fields} item={item} onSubmit={save} testid="edit-form-sheet" />
      <MovementForm open={adding} onOpenChange={setAdding} types={moveTypes} currency={item.currency} onSubmit={addMove} />
    </div>
  );
};
